/**
 * The drive client — bak's path-addressed adapter architecture, pointed at
 * sak's own vault surface (docs/architecture/bak-single-file-ui.md).
 *
 * bak addresses every entry by path and keeps a live path↔id index over the
 * drive's id-based rows (`GET /folders?flat=1` answers the owner's whole
 * folder set in one request); this port keeps that shape so the UI never
 * touches ids, with the suite-local differences that matter here: calls ride
 * the user's bearer token (localStorage `auth_token`) instead of the hub
 * cookie, every URL keeps the drive's own `/api` shape on the pooled origin,
 * and trash is a first-class view backed by the drive's trash endpoints
 * rather than bak's virtual `/.trash` namespace.
 */

import { API_URL } from '@/lib/api';

/** A folder row of the vault (`kap_folder`, internals trimmed server-side). */
interface FolderRow {
  id: string;
  name: string;
  parentId: string | null;
  createdAt?: string;
  updatedAt?: string;
}

/** A file row of the vault (`kap_file`, key/iv/hash/owner stripped server-side). */
interface FileRow {
  id: string;
  name: string;
  parentId: string | null;
  mimeType?: string;
  size: number;
  deletedAt?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface DriveEntry {
  kind: 'folder' | 'file';
  id: string;
  name: string;
  /** Absolute drive path — the address every client call takes. */
  path: string;
  parentId: string | null;
  size: number;
  mimeType?: string;
  createdAt: number;
  updatedAt: number;
}

export interface DriveTrashEntry {
  id: string;
  name: string;
  size: number;
  mimeType?: string;
  deletedAt: number;
}

export interface DriveTreeNode {
  id: string;
  name: string;
  path: string;
  children: DriveTreeNode[];
}

export class DriveMissingError extends Error {
  constructor(path: string) {
    super(`Not found on the drive: ${path}`);
    this.name = 'DriveMissingError';
  }
}

const ROOT = '/';

function normalizePath(path: string): string {
  const trimmed = String(path ?? '').trim().replace(/\\/g, '/');
  if (!trimmed || trimmed === '.') return ROOT;
  const withSlash = trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
  const collapsed = withSlash.replace(/\/{2,}/g, '/');
  return collapsed.length > 1 ? collapsed.replace(/\/+$/, '') : ROOT;
}

function parentOf(path: string): string {
  const normalized = normalizePath(path);
  return normalized.slice(0, normalized.lastIndexOf('/')) || ROOT;
}

function nameOf(path: string): string {
  return normalizePath(path).slice(normalizePath(path).lastIndexOf('/') + 1);
}

function toMs(iso?: string | null): number {
  if (!iso) return 0;
  const ms = Date.parse(iso);
  return Number.isFinite(ms) ? ms : 0;
}

/** The drive answers every failure as `{ error }`; surface it as a thrown message. */
async function vaultFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = localStorage.getItem('auth_token');
  const res = await fetch(`${API_URL}/api${path}`, {
    ...init,
    headers: {
      Accept: 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(init.body && !(init.body instanceof FormData)
        ? { 'Content-Type': 'application/json' }
        : {}),
      ...(init.headers || {}),
    },
    cache: 'no-store',
  });
  if (!res.ok) {
    let message = `Request failed with ${res.status}`;
    try {
      const body = await res.json();
      message = body?.error || body?.message || message;
    } catch {
      /* the answer was not JSON — keep the status */
    }
    throw new Error(message);
  }
  return (await res.json()) as T;
}

class DriveClient {
  private folderRows: FolderRow[] = [];
  private folderIdByPath = new Map<string, string>();
  private filesByParent = new Map<string | null, FileRow[]>();

  /* ------------------------------------------------------------ the index */

  /** The owner's whole folder set, in one request. `force` re-reads it. */
  private async ensureFolders(force = false): Promise<FolderRow[]> {
    if (force || this.folderRows.length === 0) {
      const data = await vaultFetch<{ folders: FolderRow[] }>('/folders?flat=1');
      this.folderRows = Array.isArray(data.folders) ? data.folders : [];
      this.rebuildFolderPaths();
      this.filesByParent.clear();
    }
    return this.folderRows;
  }

  /** Path → id over the flat set. First row wins on duplicate sibling names. */
  private rebuildFolderPaths(): void {
    this.folderIdByPath.clear();
    const byParent = new Map<string | null, FolderRow[]>();
    this.folderRows.forEach((row) => {
      const list = byParent.get(row.parentId ?? null) ?? [];
      list.push(row);
      byParent.set(row.parentId ?? null, list);
    });
    const walk = (parentId: string | null, parentPath: string) => {
      for (const row of byParent.get(parentId) ?? []) {
        const path = parentPath === ROOT ? `/${row.name}` : `${parentPath}/${row.name}`;
        if (!this.folderIdByPath.has(path)) this.folderIdByPath.set(path, row.id);
        walk(row.id, path);
      }
    };
    walk(null, ROOT);
  }

  private invalidate(): void {
    this.filesByParent.clear();
  }

  private async invalidateFilesOf(parentId: string | null): Promise<void> {
    this.filesByParent.delete(parentId);
    // A moved folder changes the path of everything beneath it — the cheap
    // honest answer is to re-read the folder set and drop the file cache.
    if (this.folderRows.length) await this.ensureFolders(true);
  }

  /** Every file of one cloud folder, walking the drive's pagination. */
  private async filesOf(parentId: string | null, force = false): Promise<FileRow[]> {
    if (!force && this.filesByParent.has(parentId)) {
      return this.filesByParent.get(parentId)!;
    }
    const collected: FileRow[] = [];
    const query = parentId ? `parentId=${encodeURIComponent(parentId)}&` : '';
    for (let page = 1; page <= 50; page += 1) {
      const data = await vaultFetch<{ files: FileRow[]; total: number }>(
        `/files?${query}limit=200&page=${page}&sortBy=name&sortOrder=ASC`,
      );
      collected.push(...(data.files ?? []));
      if (collected.length >= (data.total ?? 0) || data.files.length === 0) break;
    }
    this.filesByParent.set(parentId, collected);
    return collected;
  }

  /* ------------------------------------------------------------ resolution */

  private async resolveFolder(path: string): Promise<string | null> {
    const normalized = normalizePath(path);
    if (normalized === ROOT) return null;
    await this.ensureFolders();
    const id = this.folderIdByPath.get(normalized);
    if (!id) throw new DriveMissingError(normalized);
    return id;
  }

  private async resolveFile(path: string): Promise<FileRow> {
    const normalized = normalizePath(path);
    if (normalized === ROOT) throw new DriveMissingError(normalized);
    const parentId = await this.resolveFolder(parentOf(normalized));
    const rows = await this.filesOf(parentId);
    const row = rows.find((candidate) => candidate.name === nameOf(normalized));
    if (!row) throw new DriveMissingError(normalized);
    return row;
  }

  /** Create every missing folder segment of a path; answers the leaf's id. */
  private async ensureFolderChain(path: string): Promise<string | null> {
    const normalized = normalizePath(path);
    if (normalized === ROOT) return null;
    await this.ensureFolders();
    const existingId = this.folderIdByPath.get(normalized);
    if (existingId) return existingId;
    const segments = normalized.split('/').filter(Boolean);
    let parentId: string | null = null;
    let current = ROOT;
    for (const segment of segments) {
      current = current === ROOT ? `/${segment}` : `${current}/${segment}`;
      const known = this.folderIdByPath.get(current);
      if (known) {
        parentId = known;
        continue;
      }
      await vaultFetch<FolderRow>('/folders', {
        method: 'POST',
        body: JSON.stringify({ name: segment, parentId }),
      });
      await this.ensureFolders(true);
      parentId = this.folderIdByPath.get(current) ?? null;
    }
    return parentId;
  }

  /* ------------------------------------------------------------ reads */

  /**
   * Re-read the folder index and answer the entries of one path. The UI
   * calls this after every mutation and on socket events; the file cache is
   * served per parent, so repeated listings of the same folder stay cheap.
   */
  async listFolder(path: string, force = false): Promise<DriveEntry[]> {
    const normalized = normalizePath(path);
    await this.ensureFolders(force);
    const folderId = await this.resolveFolder(normalized);
    const [files, childRows] = await Promise.all([
      this.filesOf(folderId, force),
      Promise.resolve(this.childFoldersOf(folderId)),
    ]);
    const folderEntries: DriveEntry[] = childRows.map((row) => ({
      kind: 'folder' as const,
      id: row.id,
      name: row.name,
      path: normalized === ROOT ? `/${row.name}` : `${normalized}/${row.name}`,
      parentId: row.parentId ?? null,
      size: 0,
      createdAt: toMs(row.createdAt),
      updatedAt: toMs(row.updatedAt ?? row.createdAt),
    }));
    const fileEntries: DriveEntry[] = files.map((row) => ({
      kind: 'file' as const,
      id: row.id,
      name: row.name,
      path: normalized === ROOT ? `/${row.name}` : `${normalized}/${row.name}`,
      parentId: row.parentId ?? null,
      size: row.size ?? 0,
      mimeType: row.mimeType,
      createdAt: toMs(row.createdAt),
      updatedAt: toMs(row.updatedAt ?? row.createdAt),
    }));
    return [...folderEntries, ...fileEntries];
  }

  private childFoldersOf(folderId: string | null): FolderRow[] {
    return this.folderRows.filter((row) => (row.parentId ?? null) === folderId);
  }

  /** Snapshot of the flat folder rows — the tree derives from it on render. */
  getFolderRows(): FolderRow[] {
    return this.folderRows;
  }

  /* ------------------------------------------------------------ writes */

  async createFolder(parentPath: string, name: string): Promise<void> {
    const parentId = await this.resolveFolder(parentPath);
    await vaultFetch<FolderRow>('/folders', {
      method: 'POST',
      body: JSON.stringify({ name, ...(parentId ? { parentId } : {}) }),
    });
    await this.ensureFolders(true);
    await this.invalidateFilesOf(parentId);
  }

  async rename(path: string, newName: string): Promise<void> {
    const normalized = normalizePath(path);
    const entry = await this.resolveEntryKind(normalized);
    if (entry.kind === 'folder') {
      await vaultFetch(`/folders/${entry.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ name: newName }),
      });
      // The renamed folder's whole subtree changed address.
      await this.ensureFolders(true);
      this.invalidate();
      return;
    }
    await vaultFetch(`/files/${entry.id}`, {
      method: 'PATCH',
      body: JSON.stringify({ name: newName }),
    });
    await this.invalidateFilesOf(await this.resolveFolder(parentOf(normalized)));
  }

  async move(path: string, newParentPath: string): Promise<void> {
    const normalized = normalizePath(path);
    const targetParentId = await this.ensureFolderChain(newParentPath);
    const entry = await this.resolveEntryKind(normalized);
    if (entry.kind === 'folder') {
      await vaultFetch(`/folders/${entry.id}/move`, {
        method: 'PATCH',
        body: JSON.stringify({ parentId: targetParentId }),
      });
      await this.ensureFolders(true);
      this.invalidate();
      return;
    }
    await vaultFetch(`/files/${entry.id}`, {
      method: 'PATCH',
      body: JSON.stringify({ parentId: targetParentId }),
    });
    await this.invalidateFilesOf(await this.resolveFolder(parentOf(normalized)));
  }

  /** Move to trash — a soft delete; files rest, folders are gone for good. */
  async delete(path: string): Promise<void> {
    const normalized = normalizePath(path);
    const entry = await this.resolveEntryKind(normalized);
    await vaultFetch(entry.kind === 'folder' ? `/folders/${entry.id}` : `/files/${entry.id}`, {
      method: 'DELETE',
    });
    if (entry.kind === 'folder') {
      await this.ensureFolders(true);
      this.invalidate();
      return;
    }
    await this.invalidateFilesOf(await this.resolveFolder(parentOf(normalized)));
  }

  private async resolveEntryKind(path: string): Promise<{ kind: 'folder' | 'file'; id: string }> {
    const normalized = normalizePath(path);
    if (normalized === ROOT) throw new Error('The drive root cannot be modified');
    await this.ensureFolders();
    const folderId = this.folderIdByPath.get(normalized);
    if (folderId) return { kind: 'folder', id: folderId };
    const row = await this.resolveFile(normalized);
    return { kind: 'file', id: row.id };
  }

  /* ------------------------------------------------------------ trash */

  async listTrash(): Promise<DriveTrashEntry[]> {
    const data = await vaultFetch<{ files: FileRow[] }>('/files/trash/list');
    return (data.files ?? []).map((row) => ({
      id: row.id,
      name: row.name,
      size: row.size ?? 0,
      mimeType: row.mimeType,
      deletedAt: toMs(row.deletedAt ?? row.updatedAt ?? row.createdAt),
    }));
  }

  async restore(fileId: string): Promise<void> {
    await vaultFetch(`/files/${fileId}/restore`, { method: 'POST' });
    await this.ensureFolders(true);
    this.invalidate();
  }

  /* ------------------------------------------------------------ content */

  async upload(file: File, parentPath: string): Promise<void> {
    const parentId = await this.resolveFolder(parentPath);
    const form = new FormData();
    form.append('file', file);
    if (parentId) form.append('parentId', parentId);
    await vaultFetch<FileRow>('/files/upload', { method: 'POST', body: form });
    await this.invalidateFilesOf(parentId);
  }

  /** Fetch the bytes with the session's bearer. */
  async readBlob(path: string): Promise<Blob> {
    const row = await this.resolveFile(path);
    const token = localStorage.getItem('auth_token');
    const res = await fetch(`${API_URL}/api/files/${row.id}/download`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) throw new Error(`Download failed with ${res.status}`);
    return res.blob();
  }

  /** Hand the browser a download of the file at a path. */
  async download(path: string): Promise<void> {
    const row = await this.resolveFile(path);
    const blob = await this.readBlob(path);
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = row.name;
    anchor.click();
    // Revoke once the consumer has taken the stream; a short grace keeps the click alive.
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  }

  /** The public side of a share link, as a copyable URL on the pooled origin. */
  async createShareLink(path: string): Promise<string> {
    const row = await this.resolveFile(path);
    const share = await vaultFetch<{ shortId: string }>(`/files/${row.id}/share`, {
      method: 'POST',
      body: JSON.stringify({}),
    });
    return `${API_URL}/api/shares/${share.shortId}/download`;
  }

  /* ------------------------------------------------------- drive parity --- */

  /** The signed-in owner's drive profile: identity plus the live quota numbers. */
  async fetchProfile(): Promise<DriveProfile> {
    return vaultFetch<DriveProfile>('/users/profile');
  }

  /** Resolve a file row's absolute drive path by walking the folder index upward. */
  private async pathOf(row: { name: string; parentId: string | null }): Promise<string> {
    await this.ensureFolders();
    const byId = new Map(this.folderRows.map((folder) => [folder.id, folder]));
    const segments: string[] = [row.name];
    let cursor = row.parentId ?? null;
    while (cursor) {
      const folder = byId.get(cursor);
      if (!folder) break;
      segments.unshift(folder.name);
      cursor = folder.parentId ?? null;
    }
    return `/${segments.join('/')}`;
  }

  /** Drive-wide name search; results come back as path-addressed entries. */
  async search(query: string): Promise<DriveEntry[]> {
    const term = query.trim();
    if (!term) return [];
    const data = await vaultFetch<{ results: FileRow[] }>(`/search?q=${encodeURIComponent(term)}`);
    return Promise.all(
      (data.results ?? []).map(async (row) => ({
        kind: 'file' as const,
        id: row.id,
        name: row.name,
        path: await this.pathOf(row),
        parentId: row.parentId ?? null,
        size: row.size ?? 0,
        mimeType: row.mimeType,
        createdAt: toMs(row.createdAt),
        updatedAt: toMs(row.updatedAt ?? row.createdAt),
      })),
    );
  }

  /* ------------------------------------------------------- trash management */

  async emptyTrash(): Promise<TrashPurgeResult> {
    const result = await vaultFetch<TrashPurgeResult>('/files/trash', { method: 'DELETE' });
    this.invalidate();
    return result;
  }

  async permanentDelete(fileId: string): Promise<TrashPurgeResult> {
    const result = await vaultFetch<TrashPurgeResult>(`/files/${fileId}/permanent`, { method: 'DELETE' });
    this.invalidate();
    return result;
  }

  /** One request, N rows: delete | restore | permanent | move. */
  async bulk(
    op: 'delete' | 'restore' | 'permanent' | 'move',
    ids: string[],
    parentId?: string | null,
  ): Promise<{ count?: number; purged?: number; releasedObjects?: number }> {
    const result = await vaultFetch<{ count?: number; purged?: number; releasedObjects?: number }>('/files/bulk', {
      method: 'POST',
      body: JSON.stringify({ op, ids, parentId }),
    });
    this.invalidate();
    return result;
  }

  /* ---------------------------------------------------------------- copy -- */

  /** Server-side duplicate of a file or a whole folder tree — no bytes are re-uploaded. */
  async copyEntry(path: string, newParentPath: string, newName?: string): Promise<void> {
    const normalized = normalizePath(path);
    const targetParentId = await this.ensureFolderChain(newParentPath);
    await this.ensureFolders();
    const folderId = this.folderIdByPath.get(normalized);
    if (folderId) {
      await vaultFetch(`/folders/${folderId}/copy`, {
        method: 'POST',
        body: JSON.stringify({ parentId: targetParentId }),
      });
      await this.ensureFolders(true);
      this.invalidate();
      return;
    }
    const row = await this.resolveFile(normalized);
    await vaultFetch(`/files/${row.id}/copy`, {
      method: 'POST',
      body: JSON.stringify({ parentId: targetParentId, ...(newName ? { name: newName } : {}) }),
    });
    await this.invalidateFilesOf(targetParentId);
  }

  /** Create every missing folder segment of a path; answers the leaf's id (public for folder uploads). */
  ensureFolder(path: string): Promise<string | null> {
    return this.ensureFolderChain(path);
  }

  /* ------------------------------------------------------ version history - */

  async listVersions(path: string): Promise<{ versions: FileVersionRow[]; current: FileVersionRow | null }> {
    const row = await this.resolveFile(path);
    return vaultFetch<{ versions: FileVersionRow[]; current: FileVersionRow | null }>(`/files/${row.id}/versions`);
  }

  async restoreVersion(path: string, versionId: string): Promise<void> {
    const row = await this.resolveFile(path);
    await vaultFetch(`/files/${row.id}/versions/${versionId}/restore`, { method: 'POST' });
    this.invalidate();
  }

  async deleteVersion(path: string, versionId: string): Promise<void> {
    const row = await this.resolveFile(path);
    await vaultFetch(`/files/${row.id}/versions/${versionId}`, { method: 'DELETE' });
  }

  async downloadVersion(path: string, versionId: string, name: string): Promise<void> {
    const row = await this.resolveFile(path);
    const token = localStorage.getItem('auth_token');
    const res = await fetch(`${API_URL}/api/files/${row.id}/versions/${versionId}/download`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) throw new Error(`Download failed with ${res.status}`);
    const url = URL.createObjectURL(await res.blob());
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = name;
    anchor.click();
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  }

  /** Upload new content for an existing file — the old bytes become a restorable version. */
  async uploadNewVersion(
    path: string,
    file: File,
    onProgress?: (sent: number, total: number) => void,
    signal?: AbortSignal,
  ): Promise<void> {
    const row = await this.resolveFile(path);
    const form = new FormData();
    form.append('file', file);
    await xhrSend(`/files/${row.id}/content`, 'PUT', form, onProgress, signal);
    this.invalidate();
  }

  /* ---------------------------------------------------------------- shares */

  async createShareWithOptions(
    path: string,
    options: ShareOptions = {},
  ): Promise<ShareRow> {
    const row = await this.resolveFile(path);
    return vaultFetch<ShareRow>('/shares', {
      method: 'POST',
      body: JSON.stringify({ fileId: row.id, ...options }),
    });
  }

  async listShares(): Promise<ShareRow[]> {
    const data = await vaultFetch<{ shares: ShareRow[] }>('/shares/list');
    return data.shares ?? [];
  }

  async revokeShare(shareId: string): Promise<void> {
    await vaultFetch(`/shares/${shareId}`, { method: 'DELETE' });
  }

  async listSharedWithMe(): Promise<SharedWithMeRow[]> {
    const data = await vaultFetch<{ shares: SharedWithMeRow[] }>('/shares/shared-with-me');
    return data.shares ?? [];
  }

  /* -------------------------------------------------------------- activity */

  async listActivity(days = 30): Promise<ActivityRow[]> {
    const data = await vaultFetch<{ activity: ActivityRow[] }>(`/activity?days=${days}`);
    return data.activity ?? [];
  }

  /* --------------------------------------------------- keys and webhooks -- */

  async listApiKeys(): Promise<ApiKeyRow[]> {
    const data = await vaultFetch<{ keys: ApiKeyRow[] }>('/keys');
    return data.keys ?? [];
  }

  async createApiKey(name: string, write: boolean): Promise<ApiKeyCreated> {
    return vaultFetch<ApiKeyCreated>('/keys', { method: 'POST', body: JSON.stringify({ name, write }) });
  }

  async revokeApiKey(keyId: string): Promise<void> {
    await vaultFetch(`/keys/${keyId}`, { method: 'DELETE' });
  }

  async listWebhooks(): Promise<WebhookRow[]> {
    const data = await vaultFetch<{ webhooks: WebhookRow[] }>('/webhooks');
    return data.webhooks ?? [];
  }

  async createWebhook(url: string, events: string[]): Promise<WebhookCreated> {
    return vaultFetch<WebhookCreated>('/webhooks', { method: 'POST', body: JSON.stringify({ url, events }) });
  }

  async deleteWebhook(webhookId: string): Promise<void> {
    await vaultFetch(`/webhooks/${webhookId}`, { method: 'DELETE' });
  }

  /* -------------------------------------------------------------- comments */

  async listComments(path: string): Promise<CommentRow[]> {
    const row = await this.resolveFile(path);
    const data = await vaultFetch<{ comments: CommentRow[] }>(`/files/${row.id}/comments`);
    return data.comments ?? [];
  }

  async addComment(path: string, body: string): Promise<CommentRow> {
    const row = await this.resolveFile(path);
    return vaultFetch<CommentRow>(`/files/${row.id}/comments`, { method: 'POST', body: JSON.stringify({ body }) });
  }

  async deleteComment(commentId: string): Promise<void> {
    await vaultFetch(`/files/comments/${commentId}`, { method: 'DELETE' });
  }

  /* --------------------------------------------------------- file requests */

  async listFileRequests(): Promise<FileRequestRow[]> {
    const data = await vaultFetch<{ requests: FileRequestRow[] }>('/files/requests');
    return data.requests ?? [];
  }

  async createFileRequest(title: string, folderId: string | null, expiresInDays?: number): Promise<FileRequestCreated> {
    return vaultFetch<FileRequestCreated>('/files/requests', {
      method: 'POST',
      body: JSON.stringify({ title, folderId, expiresInDays }),
    });
  }

  async deleteFileRequest(requestId: string): Promise<void> {
    await vaultFetch(`/files/requests/${requestId}`, { method: 'DELETE' });
  }

  /* ---------------------------------------------------------------- billing */

  async listBillingPlans(): Promise<BillingPlans> {
    return vaultFetch<BillingPlans>('/billing/plans');
  }

  async startBillingCheckout(plan: string): Promise<{ url: string }> {
    return vaultFetch<{ url: string }>('/billing/checkout', { method: 'POST', body: JSON.stringify({ plan }) });
  }

  /* --------------------------------------------------------- delta support */

  /** Block hashes of the current content — the sync engine's delta manifest basis. */
  async listBlocks(path: string, blockSize?: number): Promise<BlockManifest> {
    const row = await this.resolveFile(path);
    const query = blockSize ? `?blockSize=${blockSize}` : '';
    return vaultFetch<BlockManifest>(`/files/${row.id}/blocks${query}`);
  }

  /* -------------------------------------------------------- content access */

  /** The raw bytes inline — the preview viewer and thumbnails read through this. */
  async readPreviewBlobById(fileId: string): Promise<Blob> {
    const token = localStorage.getItem('auth_token');
    const res = await fetch(`${API_URL}/api/preview/${fileId}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) throw new Error(`Preview failed with ${res.status}`);
    return res.blob();
  }

  /** Upload with progress events; XHR because fetch cannot report upload progress. */
  async uploadWithProgress(
    file: File,
    parentPath: string,
    onProgress?: (sent: number, total: number) => void,
    signal?: AbortSignal,
  ): Promise<void> {
    const parentId = await this.resolveFolder(parentPath);
    const form = new FormData();
    form.append('file', file);
    if (parentId) form.append('parentId', parentId);
    await xhrSend('/files/upload', 'POST', form, onProgress, signal);
    await this.invalidateFilesOf(parentId);
  }

  /** Resumable path: session + fixed-size chunks, so large files survive a dropped request
   *  and never have to fit one request body. */
  async uploadResumable(
    file: File,
    parentPath: string,
    onProgress?: (sent: number, total: number) => void,
    signal?: AbortSignal,
  ): Promise<void> {
    const parentId = await this.resolveFolder(parentPath);
    const session = await vaultFetch<{ sessionId: string; chunkSize: number; exists: boolean }>('/files/upload/session', {
      method: 'POST',
      body: JSON.stringify({
        name: file.name,
        size: file.size,
        mimeType: file.type || 'application/octet-stream',
        parentId,
      }),
    });
    if (session.exists) {
      await this.invalidateFilesOf(parentId);
      return;
    }
    const total = file.size;
    const chunkCount = Math.max(1, Math.ceil(total / session.chunkSize));
    for (let index = 0; index < chunkCount; index += 1) {
      const start = index * session.chunkSize;
      const form = new FormData();
      form.append('chunk', file.slice(start, Math.min(start + session.chunkSize, total)));
      await xhrSend(
        `/files/upload/session/${session.sessionId}/chunk?index=${index}`,
        'PUT',
        form,
        (sent) => onProgress?.(start + sent, total),
        signal,
      );
    }
    await vaultFetch(`/files/upload/session/${session.sessionId}/complete`, {
      method: 'POST',
      body: JSON.stringify({ name: file.name, mimeType: file.type || 'application/octet-stream', parentId }),
    });
    await this.invalidateFilesOf(parentId);
  }
}

/* ------------------------------------------------------------ shared types */

/** The signed-in owner's drive profile: identity plus the live quota numbers. */
export interface DriveProfile {
  user: { id: string; email: string; name: string; role: string; storageUsed: number; storageLimit: number };
  quota: { used: string; hard: string; over: boolean };
}

export interface TrashPurgeResult {
  purged: number;
  releasedObjects: number;
}

export interface FileVersionRow {
  id: string;
  name: string;
  mimeType: string;
  size: number;
  version: number;
  createdAt?: string;
}

export interface ShareOptions {
  password?: string;
  expiresInDays?: number;
  maxDownloads?: number;
  allowDownload?: boolean;
  sharedWithEmail?: string;
}

export interface ShareRow {
  id: string;
  fileId: string;
  shortId: string;
  expiresAt?: string | null;
  allowDownload: boolean;
  maxDownloads?: number | null;
  downloads: number;
  createdAt?: string;
  requiresPassword?: boolean;
  sharedWithEmail?: string | null;
  fileName?: string | null;
  fileSize?: number | null;
}

export interface SharedWithMeRow {
  id: string;
  shortId: string;
  createdAt?: string;
  expiresAt?: string | null;
  allowDownload: boolean;
  requiresPassword?: boolean;
  ownerName?: string | null;
  ownerEmail?: string | null;
  fileName?: string | null;
  fileSize?: number | null;
  fileMimeType?: string | null;
}

export interface ActivityRow {
  id: string;
  type: string;
  description: string;
  createdAt: string;
}

export interface ApiKeyRow {
  id: string;
  name: string;
  keyPrefix: string;
  scopes: string;
  createdAt?: string;
  lastUsedAt?: string | null;
  revokedAt?: string | null;
}

export interface ApiKeyCreated {
  id: string;
  name: string;
  scopes: string[];
  key: string;
  prefix: string;
}

export interface WebhookRow {
  id: string;
  url: string;
  events: string;
  active: boolean;
  failureCount: number;
  lastStatus?: number | null;
  createdAt?: string;
}

export interface WebhookCreated extends WebhookRow {
  secret: string;
}

export interface CommentRow {
  id: string;
  fileId: string;
  userId: string;
  userName: string;
  body: string;
  createdAt?: string;
}

export interface FileRequestRow {
  id: string;
  shortId: string;
  title: string;
  active: boolean;
  expiresAt?: string | null;
  submissions: number;
  createdAt?: string;
  folderName?: string | null;
}

export interface FileRequestCreated {
  id: string;
  shortId: string;
  title: string;
  folderId: string | null;
}

export interface BillingPlanInfo {
  id: string;
  name: string;
  bytes: number;
  priceMonthly: number | null;
}

export interface BillingPlans {
  plans: BillingPlanInfo[];
  current: {
    plan: string;
    planName: string;
    quotaBytes: number;
    customerId: string | null;
    subscriptionId: string | null;
    hasPaymentProvider: boolean;
  };
}

export interface BlockManifest {
  fileId: string;
  size: number;
  blockSize: number;
  blocks: Array<{ index: number; hash: string }>;
}

/** XHR with upload progress — the one thing fetch cannot do. */
function xhrSend<T = unknown>(
  path: string,
  method: string,
  body: FormData,
  onProgress?: (sent: number, total: number) => void,
  signal?: AbortSignal,
): Promise<T> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(method, `${API_URL}/api${path}`);
    const token = localStorage.getItem('auth_token');
    if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) onProgress?.(event.loaded, event.total);
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          resolve(JSON.parse(xhr.responseText) as T);
        } catch {
          resolve(undefined as T);
        }
        return;
      }
      let message = `Request failed with ${xhr.status}`;
      try {
        const parsed = JSON.parse(xhr.responseText) as { error?: string; message?: string };
        message = parsed?.error || parsed?.message || message;
      } catch {
        /* keep the status message */
      }
      reject(new Error(message));
    };
    xhr.onerror = () => reject(new Error('Network error during upload'));
    xhr.onabort = () => reject(new DOMException('Aborted', 'AbortError'));
    signal?.addEventListener('abort', () => xhr.abort(), { once: true });
    xhr.send(body);
  });
}

export const driveClient = new DriveClient();
